(() => {
  // Loaded per section and re-executed when quick view injects its markup;
  // a second submit listener would send duplicate subscriptions.
  if (window.backInStockKlaviyoLoaded) return;
  window.backInStockKlaviyoLoaded = true;

  const FORM_SELECTOR = 'form[data-klaviyo-back-in-stock]';
  const FALLBACK_COMPANY_ID = 'WMSE7Z';
  const API_REVISION = '2026-07-15';
  const API_BASE_URL = 'https://a.klaviyo.com/client';
  const SHOPIFY_VARIANT_PREFIX = '$shopify:::$default:::';
  const RAW_PHONE_PROPERTY = 'back_in_stock_phone';
  // Countries whose national numbers drop a leading 0 after the dial code.
  const DIAL_CODES = { NL: '31', BE: '32', FR: '33', DE: '49', AT: '43', LU: '352' };

  const pendingForms = new WeakSet();

  function getCompanyId() {
    const scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (const script of scripts) {
      const match = script.src.match(/company_id=([A-Za-z0-9]+)/);
      if (match) return match[1];
    }
    return FALLBACK_COMPANY_ID;
  }

  // The product form's variant input tracks variant switches; data-variant-id is
  // the server-rendered selection, used when the form isn't on the page.
  function getVariantId(form) {
    const productForm = document.getElementById(form.dataset.productFormId || '');
    const variantInput = productForm && productForm.querySelector('[name="id"]');
    const variantId = (variantInput && variantInput.value) || form.dataset.variantId || '';
    return /^\d+$/.test(variantId) ? variantId : '';
  }

  function splitName(name) {
    const [firstName, ...lastNames] = name.split(/\s+/).filter(Boolean);
    const attributes = {};
    if (firstName) attributes.first_name = firstName;
    if (lastNames.length) attributes.last_name = lastNames.join(' ');
    return attributes;
  }

  function toE164(phone) {
    let number = phone.replace(/[\s().\-/]/g, '');
    if (number.startsWith('00')) number = `+${number.slice(2)}`;

    if (!number.startsWith('+')) {
      const country = window.Shopify && window.Shopify.country;
      const dialCode = DIAL_CODES[country];
      if (!dialCode || !number.startsWith('0')) return '';
      number = `+${dialCode}${number.slice(1)}`;
    }

    return /^\+[1-9]\d{7,14}$/.test(number) ? number : '';
  }

  function buildProfilePayload(attributes) {
    return {
      data: {
        type: 'profile',
        attributes,
      },
    };
  }

  function buildSubscriptionPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `${SHOPIFY_VARIANT_PREFIX}${variantId}`,
            },
          },
        },
      },
    };
  }

  async function postToKlaviyo(path, payload) {
    const response = await fetch(`${API_BASE_URL}/${path}/?company_id=${encodeURIComponent(getCompanyId())}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: API_REVISION,
      },
      body: JSON.stringify(payload),
    });

    if (response.status !== 202) {
      const error = new Error(`Klaviyo ${path} responded with ${response.status}: ${await response.text()}`);
      error.status = response.status;
      throw error;
    }
  }

  async function saveProfileDetails(email, name, phone) {
    if (!name && !phone) return;

    const attributes = { email, ...splitName(name) };
    // Klaviyo answers 202 but silently drops phone numbers it considers invalid,
    // so the customer's input is always kept as a plain property as well.
    if (phone) attributes.properties = { [RAW_PHONE_PROPERTY]: phone };

    const phoneNumber = toE164(phone);
    if (phoneNumber) attributes.phone_number = phoneNumber;

    try {
      await postToKlaviyo('profiles', buildProfilePayload(attributes));
    } catch (error) {
      if (!phoneNumber || !(error.status >= 400 && error.status < 500)) throw error;

      const { phone_number: _rejected, ...rest } = attributes;
      await postToKlaviyo('profiles', buildProfilePayload(rest));
    }
  }

  function setStatus(form, status) {
    const container = form.querySelector('[data-back-in-stock-status]');
    if (!container) return;

    const template = status && form.querySelector(`template[data-back-in-stock-${status}]`);
    if (!template) {
      container.replaceChildren();
      container.hidden = true;
      return;
    }

    container.replaceChildren(template.content.cloneNode(true));
    container.hidden = false;
  }

  function setBusy(form, busy) {
    form.querySelectorAll('[type="submit"]').forEach((button) => {
      button.disabled = busy;
      if (busy) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  }

  function getFieldValue(form, name) {
    const field = form.querySelector(`[name="${name}"]`);
    return field ? field.value.trim() : '';
  }

  async function subscribe(form) {
    const email = getFieldValue(form, 'contact[email]');
    const name = getFieldValue(form, 'contact[name]');
    const phone = getFieldValue(form, 'contact[phone]');
    const variantId = getVariantId(form);

    if (!email || !variantId) {
      console.error('Back in stock: missing email or variant id', { email: Boolean(email), variantId });
      setBusy(form, false);
      setStatus(form, 'error');
      return;
    }

    pendingForms.add(form);
    setStatus(form, null);
    setBusy(form, true);

    try {
      await saveProfileDetails(email, name, phone);
      await postToKlaviyo('back-in-stock-subscriptions', buildSubscriptionPayload(email, variantId));

      form.reset();
      setStatus(form, 'success');
    } catch (error) {
      console.error('Back in stock: Klaviyo subscription failed', error);
      setStatus(form, 'error');
    } finally {
      pendingForms.delete(form);
      setBusy(form, false);
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

    event.preventDefault();
    if (pendingForms.has(form)) return;
    subscribe(form);
  });
})();
